import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const tempId = () => `_new_${Math.random().toString(36).slice(2, 10)}`;

/**
 * Homepage-builder style editor for ordered content modules.
 * Left: drag list · Center: compact preview · Right: inspector
 *
 * Optional:
 * - getApiId(item) → path segment for PATCH/DELETE (defaults to item.id)
 * - preparePayload(item) → body sent on create/update
 * - normalizeItem(item) → transform after load
 * - columnsClass → grid layout override
 * - inspectorScroll → make inspector scroll independently
 *
 * Full view: inspector expands to full width (hides list + preview).
 * renderInspector(item, patch, items, { fullView })
 */
export default function OrderedModuleEditor({
  resource,
  singular = "item",
  title,
  subtitle,
  titleKey = "title",
  defaultValues = {},
  renderInspector,
  renderPreviewItem,
  getItemLabel,
  getApiId,
  preparePayload,
  normalizeItem,
  columnsClass = "xl:grid-cols-[280px_1fr_320px]",
  inspectorScroll = false,
  /** When true, each item gets published/draft status (honored by public /api/site). */
  enableStatus = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletedKeys, setDeletedKeys] = useState([]);
  const [fullView, setFullView] = useState(false);

  const apiIdOf = (item) => {
    if (getApiId) return getApiId(item);
    return item?.id;
  };

  const labelOf = (item, idx) => {
    if (getItemLabel) return getItemLabel(item, idx);
    return item?.[titleKey] || item?.name || item?.label || `${singular} ${idx + 1}`;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${resource}`, { params: { page_size: 200 } });
      let list = (data.items || data || [])
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (normalizeItem) list = list.map(normalizeItem);
      setItems(list);
      setDeletedKeys([]);
      setSaved(true);
      setSelectedId(list[0]?.id || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [resource]); // eslint-disable-line

  useEffect(() => {
    const onLeave = (e) => {
      if (saved) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [saved]);

  const markDirty = (next) => {
    setItems(next);
    setSaved(false);
  };

  const addItem = () => {
    const id = tempId();
    const maxOrder = items.reduce((m, it) => Math.max(m, Number(it.order) || 0), 0);
    const next = [...items, {
      ...defaultValues,
      ...(enableStatus ? { status: defaultValues.status || "published" } : {}),
      id,
      order: Math.max(maxOrder, items.length) + 1,
    }];
    markDirty(next);
    setSelectedId(id);
  };

  const updateItem = (id, patch) => {
    markDirty(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const moveTo = (from, to) => {
    if (from === to || from < 0 || to < 0 || to >= items.length) return;
    const next = [...items];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    markDirty(next.map((it, i) => ({ ...it, order: i + 1 })));
  };

  const requestDelete = (item) => setPendingDelete(item);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    if (id && !String(id).startsWith("_new_")) {
      const key = apiIdOf(pendingDelete);
      if (key) setDeletedKeys((prev) => [...prev, key]);
    }
    const next = items.filter((it) => it.id !== id).map((it, i) => ({ ...it, order: i + 1 }));
    markDirty(next);
    if (selectedId === id) setSelectedId(next[0]?.id || null);
    setPendingDelete(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(deletedKeys.map((key) => api.delete(`/${resource}/${key}`)));

      const stripMeta = (it) => {
        const {
          id,
          _id,
          created_at,
          updated_at,
          _originalSlug,
          ...rest
        } = it;
        return rest;
      };

      const ordered = items.map((it, i) => ({ ...it, order: i + 1 }));
      for (const it of ordered) {
        const { id } = it;
        const rest = preparePayload ? preparePayload(it) : stripMeta(it);
        if (String(id).startsWith("_new_")) {
          await api.post(`/${resource}`, { ...defaultValues, ...rest });
        } else {
          const key = apiIdOf(it);
          await api.patch(`/${resource}/${key}`, rest);
        }
      }
      toast.success("Saved");
      await load();
    } catch (e) {
      toast.error(e?.message || formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const selected = items.find((it) => it.id === selectedId);

  if (loading) {
    return <div className="text-white/50 py-12 text-center text-sm">Loading…</div>;
  }

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            <span className={`text-xs ${saved ? "text-emerald-300" : "text-amber-300"}`}>
              {saved ? "All changes saved" : "Unsaved changes"}
            </span>
            <Button onClick={addItem} variant="ghost" className="text-white/80 border border-white/10">
              <Icons.Plus className="w-4 h-4 mr-1.5" /> Add {singular}
            </Button>
            <Button onClick={save} disabled={saving || saved} className="btn-gradient text-white border-0">
              <Icons.Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </>
        }
      />

      <div className={`grid grid-cols-1 gap-4 ${fullView ? "" : columnsClass}`}>
        {/* Ordered list */}
        {!fullView && (
        <GlassCard className="p-4">
          <h3 className="text-xs uppercase tracking-wider text-white/50 mb-1">Items</h3>
          <p className="text-[11px] text-white/40 mb-3">Drag to reorder. Select an item to edit.</p>
          <div className="space-y-1">
            {items.map((it, idx) => (
              <div
                key={it.id}
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIndex != null) moveTo(dragIndex, idx); setDragIndex(null); }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs cursor-grab active:cursor-grabbing ${
                  selectedId === it.id ? "bg-white/10" : "hover:bg-white/[0.03]"
                } ${dragIndex === idx ? "opacity-50" : ""}`}
              >
                <Icons.GripVertical className="w-3.5 h-3.5 text-white/35 shrink-0" aria-hidden="true" />
                <button type="button" onClick={() => setSelectedId(it.id)} className="flex-1 text-left text-white/80 truncate">
                  {idx + 1}. {labelOf(it, idx)}
                  {enableStatus && (it.status || "published") === "draft" && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-300/90">draft</span>
                  )}
                </button>
                <button type="button" onClick={() => moveTo(idx, idx - 1)} disabled={idx === 0} className="p-1 text-white/40 hover:text-white disabled:opacity-20" aria-label="Move up">
                  <Icons.ChevronUp className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => moveTo(idx, idx + 1)} disabled={idx === items.length - 1} className="p-1 text-white/40 hover:text-white disabled:opacity-20" aria-label="Move down">
                  <Icons.ChevronDown className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => requestDelete(it)} className="p-0.5 text-white/40 hover:text-red-300" aria-label="Delete">
                  <Icons.Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {items.length === 0 && (
              <p className="text-xs text-white/40 py-6 text-center">No {singular}s yet. Add one to get started.</p>
            )}
          </div>
        </GlassCard>
        )}

        {/* Preview */}
        {!fullView && (
        <GlassCard className="p-6 min-h-[420px]">
          <h3 className="text-xs uppercase tracking-wider text-white/50 mb-4">Preview</h3>
          {items.length === 0 ? (
            <div className="text-center text-white/40 py-24 text-sm">Add items from the left panel</div>
          ) : (
            <div className="space-y-2">
              {items.map((it, idx) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setSelectedId(it.id)}
                  className={`w-full text-left rounded-xl border border-white/10 p-4 transition-colors ${
                    selectedId === it.id ? "bg-white/[0.06]" : "bg-white/[0.02] hover:bg-white/[0.04]"
                  }`}
                >
                  {renderPreviewItem ? renderPreviewItem(it, idx) : (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">#{idx + 1}</div>
                      <div className="text-sm font-medium text-white">{labelOf(it, idx)}</div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </GlassCard>
        )}

        {/* Inspector */}
        <GlassCard className={`p-4 sm:p-6 h-fit w-full ${fullView ? "max-w-[1600px]" : ""} ${inspectorScroll || fullView ? "max-h-[calc(100vh-160px)] overflow-y-auto" : ""}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-xs uppercase tracking-wider text-white/50">
              {fullView ? `Edit ${singular}` : "Inspector"}
            </h3>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-white/70 hover:text-white h-8 px-2.5 border border-white/15 bg-white/[0.04] hover:bg-white/10"
              onClick={() => setFullView((v) => !v)}
              disabled={!selected && !fullView}
              title={fullView ? "Collapse" : "Expand"}
              aria-pressed={fullView}
            >
              {fullView ? (
                <><Icons.Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Collapse</>
              ) : (
                <><Icons.Maximize2 className="w-3.5 h-3.5 mr-1.5" /> Expand</>
              )}
            </Button>
          </div>
          {fullView && items.length > 0 && (
            <div className="mb-4">
              <label className="text-[11px] text-white/40 block mb-1">Switch {singular}</label>
              <select
                value={selectedId || ""}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2"
              >
                {items.map((it, idx) => (
                  <option key={it.id} value={it.id}>{idx + 1}. {labelOf(it, idx)}</option>
                ))}
              </select>
            </div>
          )}
          {selected ? (
            <div className={fullView ? "space-y-4" : "space-y-3"}>
              {enableStatus && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                  <div>
                    <div className="text-xs text-white/70">Visibility</div>
                    <p className="text-[11px] text-white/40">Draft items stay off the live website.</p>
                  </div>
                  <select
                    value={selected.status || "published"}
                    onChange={(e) => updateItem(selected.id, { status: e.target.value })}
                    className="h-9 rounded-md bg-black/30 border border-white/10 text-white text-xs px-2"
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              )}
              {renderInspector(selected, (patch) => updateItem(selected.id, patch), items, { fullView })}
            </div>
          ) : (
            <p className="text-xs text-white/40">Select an item to edit.</p>
          )}
        </GlassCard>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent className="glass-strong border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {singular}?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              “{pendingDelete ? labelOf(pendingDelete, 0) : ""}” will be removed when you save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
